/**
 * Bilingual field pair — used by every content form (W-011, W-021, W-051,
 * W-060, W-080, W-081, W-091).
 *
 * docs/03 §4.1: every user-facing string is two sibling columns, `x_ar` and
 * `x_fr`. The forms map onto them without indirection, so this renders the two
 * inputs side by side rather than behind a language tab — a content manager
 * comparing the two languages is the normal case, not the exception.
 *
 * Arabic renders right-to-left even while the admin chrome is French (docs/04
 * §7.1): the value is Arabic, the surrounding UI is not.
 */
import { Col, Form, Input, Row } from 'antd'
import type { Rule } from 'antd/es/form'
import { useAdminTranslate } from '@/providers/i18n-provider'

export interface BilingualFieldProps {
  /** Base name: `name` produces `name_ar` and `name_fr`. */
  name: string
  label: string
  /** Multiline input, e.g. descriptions. */
  multiline?: boolean
  rows?: number
  /**
   * Required for a draft. Arabic is the source language, so Arabic is required
   * at save time and French is required only at publish time (US-A02 AC6,
   * enforced in `lib/bilingual.ts`).
   */
  requiredAr?: boolean
  maxLength?: number
  disabled?: boolean
}

export const BilingualField = ({
  name,
  label,
  multiline = false,
  rows = 4,
  requiredAr = false,
  maxLength,
  disabled = false,
}: BilingualFieldProps) => {
  const translate = useAdminTranslate()
  const arRules: Rule[] = requiredAr
    ? [{ required: true, message: translate('admin.bilingual.arabicRequired', { label }) }]
    : []

  const control = (dir: 'rtl' | 'ltr') =>
    multiline ? (
      <Input.TextArea
        rows={rows}
        maxLength={maxLength}
        disabled={disabled}
        showCount={!!maxLength}
        dir={dir}
      />
    ) : (
      <Input maxLength={maxLength} disabled={disabled} dir={dir} />
    )

  return (
    <Row gutter={16}>
      <Col xs={24} md={12}>
        {/* The stored value is Arabic; render it RTL whatever the chrome does. */}
        <Form.Item
          name={`${name}_ar`}
          label={translate('admin.bilingual.arabicLabel', { label })}
          rules={arRules}
        >
          {control('rtl')}
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item name={`${name}_fr`} label={translate('admin.bilingual.frenchLabel', { label })}>
          {control('ltr')}
        </Form.Item>
      </Col>
    </Row>
  )
}
